/**
 * flogpt.com on-page copy for the KW3 keyword targets.
 *
 * Every getter returns null on the India build (`VITE_SITE_REGION=in`), and
 * the callers keep the previous title, H1, and intro. The check is inside
 * the function so a test can flip the env before render. Do not edit the
 * inbound-voice how-to (`inboundVoiceGuide.ts`); this module only links to it.
 */

import { INBOUND_VOICE_GUIDE_PATH, INBOUND_VOICE_OVERVIEW_SLUG } from "./inboundVoiceGuide";

export const comMarketing = (): boolean => import.meta.env.VITE_SITE_REGION !== "in";

export type Kw3LinkPart = { text: string } | { href: string; anchor: string };

export type Kw3Blog = {
    seoTitle?: string;
    metaDescription?: string;
    h1?: string;
    /** Paragraph inserted after the summary, before the stored HTML. */
    lead?: string;
    h2?: { heading: string; body: string };
    linkLine?: Kw3LinkPart[];
    /** Replace the brand misspelling "Flogpt" in the stored HTML and summary. */
    fixBrandSpelling?: boolean;
};

const HOTEL_BLOG_SLUG = "hotel-ai-voice-bot-for-front-desk-improve-guest-communication-with-flogpt";
const PHONE_BLOG_SLUG = "automated-phone-calls-with-voice-ai-agents";
const SHOPIFY_BLOG_SLUG = "customer-service-for-shopify-a-complete-guide-for-store-owners";
const GUEST_BLOG_SLUG = "how-ai-guest-communication-tools-are-transforming-the-hospitality-industry";

export const KW3_CHAT_WIDGET = {
    title: "AI Chat Widget for Websites — Live in 5 Minutes | FloGPT",
    description:
        "Add an AI chat widget to any website in under five minutes. It answers from your knowledge base in 50+ languages and hands off to a human. Free plan.",
    h1Lead: "The AI Chat Widget Your Customers Actually",
    intro: "FloGPT's AI chat widget embeds on any website with one script tag and answers from your own docs, FAQs and PDFs.",
    follow: "It is brandable, streams replies, and hands the conversation to a person when the question needs one.",
};

export const KW3_COMPARE = {
    title: "Synthflow vs Vapi, Bland & Retell: Voice AI | FloGPT",
    description:
        "Synthflow vs Vapi, Bland and Retell side by side, and how FloGPT adds web chat and a seven-channel CRM inbox to voice AI.",
    h2: "Synthflow vs Vapi",
};

/** Hub table. Chat cells match the Synthflow and Vapi comparison pages. */
export const KW3_SYNTHFLOW_VAPI_ROWS: { label: string; synthflow: string; vapi: string }[] = [
    {
        label: "Who it's for",
        synthflow: "Non-technical teams who want a no-code voice assistant, with other channels and a CRM already covered elsewhere.",
        vapi: "Engineering teams who want code-first control and plan to build the surrounding product (UI, storage, CRM) themselves.",
    },
    {
        label: "Build style",
        synthflow: "No-code builder for launching a voice assistant without engineering.",
        vapi: "APIs and SDKs: you assemble speech-to-text, the model, and text-to-speech in code.",
    },
    {
        label: "What you need to add",
        synthflow: "A unified ticket inbox. Chat agents (widget, API, SMS, WhatsApp) are set up separately from voice.",
        vapi: "A chat front end on its Chat API, a CRM inbox, and your own knowledge store.",
    },
];

export const KW3_INDUSTRY: Record<
    string,
    {
        title?: string;
        description?: string;
        headline?: string;
        lead?: string;
        subhead?: string;
        section?: {
            h2: string;
            /** Substring of `h2` rendered as a link. */
            h2Link?: { phrase: string; href: string };
            body: string;
            /** Substring of `body` rendered as a link. */
            bodyLink?: { phrase: string; href: string };
        };
    }
> = {
    "home-services": {
        title: "Home Services AI: HVAC, Plumbing & Electrical Calls | FloGPT",
        headline: "Home Services AI: Voice + Chat That Books Jobs",
        lead: "FloGPT is home services AI that answers every call, triages emergencies and books the job.",
    },
    insurance: {
        title: "Insurance Chat Platform + AI Voice for Agencies | FloGPT",
        description:
            "An insurance chat platform and voicebot for agencies: captures quote leads, routes claims and syncs to AMS360 / Applied Epic. Never quotes prices.",
        headline: "AI Voice + Chat Platform for Insurance Agencies",
        section: {
            h2: "An insurance chat platform for web, SMS and WhatsApp",
            body: "Quote intake runs on the website chat widget, on SMS, and on WhatsApp. The agent captures the line of insurance, the current carrier, and the renewal date, routes an active claim to a person, and never quotes a price.",
        },
    },
    logistics: {
        title: "Voice Logistics API for Shipping | FloGPT",
        section: {
            h2: "Voice AI for logistics, via API and TMS integrations",
            body: "A voice logistics API here is the REST API plus the TMS webhook the agent already uses: it looks up tracking, confirms a delivery change, and sends a pickup request.",
            bodyLink: { phrase: "REST API", href: "/docs/get-started/setup" },
        },
    },
    restaurants: {
        title: "Restaurant Call Bot: Reservations & Waitlist 24/7 | FloGPT",
        headline: "Restaurant Call Bot + AI Chat for Reservations",
        lead: "A restaurant call bot takes reservations and waitlist signups while the host stays on the floor.",
    },
    hospitality: {
        title: "Multilingual AI Voice & Chat for Hotels & Rentals | FloGPT",
        subhead: "AI guest communication",
    },
};

export const KW3_SHOPIFY = {
    title: "Shopify AI Chatbot + Voice Support, 24/7 | FloGPT",
    intro: "A Shopify AI chatbot answers order, refund, and product questions in chat, and the same agent handles the phone line.",
};

export const KW3_PIPELINE = {
    h2: "A visual call flow editor",
    body: "The visual call flow editor sets the greeting, the lookups, and the transfer, and each publish is a version you can roll back.",
    editorAlt: "FloGPT Call Pipelines list with lead qualification, appointment booking and support triage call flows",
    editorWidth: 1024,
    editorHeight: 702,
};

export const KW3_VOICE = {
    title: "AI Voice Agents for Inbound & Outbound Calls 24/7 | FloGPT",
    h2: "Outbound AI agent for reminders, follow-ups and lead callbacks",
    sentences: [
        "An outbound AI agent places reminder calls, follow-ups, and callbacks to leads who asked to be reached.",
        "Webhooks, Shopify events, schedules, and uploaded lists start those calls on the same voice agent that answers the phone, with quiet hours and retry rules on the trigger.",
    ],
};

const BLOGS: Record<string, Kw3Blog> = {
    [INBOUND_VOICE_OVERVIEW_SLUG]: {
        seoTitle: "AI Inbound Call Handling with Voice Agents | FloGPT Blog",
        metaDescription:
            "How an AI inbound call works: a voice agent answers, qualifies, books and transfers 24/7. Plus outbound use cases and setup steps.",
        h1: "AI Voice Agents for Smarter Inbound and Outbound Calling",
        h2: {
            heading: "How an AI inbound call works",
            body: "The voice agent answers, reads the caller's intent, looks up an account or books the appointment, and transfers to a person when a human should take over.",
        },
        linkLine: [
            { text: "You can " },
            { href: INBOUND_VOICE_GUIDE_PATH, anchor: "set up an AI voice agent for inbound calls" },
            { text: ", and the " },
            { href: "/ai-voice-agents", anchor: "outbound AI agent" },
            { text: " page covers reminders and callbacks." },
        ],
    },
    [PHONE_BLOG_SLUG]: {
        seoTitle: "Automated Phone Agent: Voice AI Handles Calls | FloGPT Blog",
        metaDescription:
            "What an automated phone agent does, where it beats IVR, and how to launch one with voice AI: answering, routing, booking and follow-ups 24/7.",
        h1: "Automated Phone Agent: How Voice AI Agents Handle Calls",
        lead: "An automated phone agent is voice AI that answers, routes, books, and follows up on calls, so the caller can speak instead of pressing a menu.",
        linkLine: [
            { text: "See " },
            { href: "/ai-voice-agents", anchor: "AI voice agents" },
            { text: " for the phone product that runs this." },
        ],
    },
    [HOTEL_BLOG_SLUG]: {
        seoTitle: "Hotel Phone Bot: AI Voice for the Front Desk | FloGPT Blog",
        metaDescription:
            "A hotel phone bot answers front-desk calls 24/7 in many languages: bookings, check-in questions, concierge requests and transfers to staff.",
        h1: "Hotel Phone Bot: AI Voice for the Front Desk",
        lead: "A hotel phone bot answers front-desk calls for bookings, check-in questions, and concierge requests, and transfers to staff when a person should take the call.",
        fixBrandSpelling: true,
    },
    [SHOPIFY_BLOG_SLUG]: {
        linkLine: [
            { text: "On the product side, see the " },
            { href: "/shopify-ai-customer-support", anchor: "Shopify AI chatbot" },
            { text: "." },
        ],
    },
    [GUEST_BLOG_SLUG]: {
        linkLine: [
            { text: "See " },
            { href: "/use-cases/hospitality", anchor: "AI guest communication" },
            { text: " for hotels and vacation rentals." },
        ],
    },
};

export function industryKw3(slug: string | undefined) {
    if (!slug || !comMarketing()) return null;
    return KW3_INDUSTRY[slug] ?? null;
}

export function kw3Blog(slug: string | undefined): Kw3Blog | null {
    if (!slug || !comMarketing()) return null;
    return BLOGS[slug] ?? null;
}

/** Visible title on public blog cards. India and unlisted slugs keep the stored title. */
export function publicBlogLabel(slug: string | undefined, title: string): string {
    return kw3Blog(slug)?.h1 || title;
}

/** The live hotel post spells the brand "Flogpt". URLs and the lowercase slug stay put. */
export function spellFloGptBrand(value: string): string {
    return value.replace(/\bFlogpt\b/g, "FloGPT");
}

export const KW3_HOTEL_BLOG_PATH = `/blog/${HOTEL_BLOG_SLUG}`;
export const KW3_PHONE_BLOG_PATH = `/blog/${PHONE_BLOG_SLUG}`;

/** Titles and meta descriptions this change introduces. Kept metas are not listed. */
export function kw3IntroducedHeadTags(): { label: string; title?: string; description?: string }[] {
    return [
        { label: "ai-chat-widget", title: KW3_CHAT_WIDGET.title, description: KW3_CHAT_WIDGET.description },
        {
            label: "inbound-blog",
            title: BLOGS[INBOUND_VOICE_OVERVIEW_SLUG].seoTitle,
            description: BLOGS[INBOUND_VOICE_OVERVIEW_SLUG].metaDescription,
        },
        { label: "compare", title: KW3_COMPARE.title, description: KW3_COMPARE.description },
        { label: "home-services", title: KW3_INDUSTRY["home-services"].title },
        {
            label: "insurance",
            title: KW3_INDUSTRY.insurance.title,
            description: KW3_INDUSTRY.insurance.description,
        },
        {
            label: "phone-blog",
            title: BLOGS[PHONE_BLOG_SLUG].seoTitle,
            description: BLOGS[PHONE_BLOG_SLUG].metaDescription,
        },
        {
            label: "hotel-blog",
            title: BLOGS[HOTEL_BLOG_SLUG].seoTitle,
            description: BLOGS[HOTEL_BLOG_SLUG].metaDescription,
        },
        { label: "logistics", title: KW3_INDUSTRY.logistics.title },
        { label: "restaurants", title: KW3_INDUSTRY.restaurants.title },
        { label: "shopify", title: KW3_SHOPIFY.title },
        { label: "voice-agents", title: KW3_VOICE.title },
        { label: "hospitality", title: KW3_INDUSTRY.hospitality.title },
    ];
}
